import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import MobileMenu from '../components/feature/MobileMenu';
import { useSEO } from '../hooks/useSEO';
import JsonLd from '../components/feature/JsonLd';

export default function NotFound() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useSEO({
    title: '404 - Page Not Found | Design For',
    description: 'The page you are looking for could not be found. Return to our homepage or explore our services, work, and insights.',
    noindex: true,
    canonical: '/404'
  });

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const suggestions = [
    { label: 'Home', to: '/' },
    { label: 'Work', to: '/work' },
    { label: 'About', to: '/about' },
    { label: 'Services', to: '/services' },
    { label: 'Contact', to: '/contact' },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "404 - Page Not Found",
        "url": "https://example.com/404",
        "description": "The page you are looking for could not be found"
      }} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png"
                alt="Design For"
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Home</Link>
              <Link to="/about" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">About</Link>
              <Link to="/services" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Services</Link>
              <Link to="/work" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Work</Link>
              <Link to="/resources" className="text-black hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Insights</Link>
              <Link to="/contact" className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                Contact
              </Link>
            </div>
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center pt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left — Text */}
            <div className="space-y-8">
              <p className="text-sm font-black text-primary uppercase tracking-wider">404 — Page not found</p>
              <h1 className="text-6xl lg:text-7xl font-black text-black tracking-tight leading-[1.05]">
                This page doesn't exist.
              </h1>
              <p className="text-lg text-gray-500 font-medium leading-relaxed max-w-md">
                The link you followed may be broken, outdated, or the page may have moved. Let's get you back on track.
              </p>

              {/* Tried URL */}
              {location.pathname && location.pathname !== '/' && (
                <div className="inline-flex items-center gap-3 bg-[#f8f8f8] rounded-xl px-5 py-3">
                  <i className="ri-link-unlink-m text-gray-400 text-base"></i>
                  <span className="text-sm text-gray-400 font-mono">{location.pathname}</span>
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full hover:bg-primary/90 transition-all duration-300 text-sm font-bold uppercase tracking-wider whitespace-nowrap group"
                >
                  Back to Home
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 border border-gray-200 text-black px-8 py-4 rounded-full hover:border-primary hover:text-primary transition-all duration-300 text-sm font-bold uppercase tracking-wider whitespace-nowrap"
                >
                  Get in Touch
                </Link>
              </div>
            </div>

            {/* Right — Large 404 + Quick Links */}
            <div className="flex flex-col items-start lg:items-end gap-10">
              {/* Giant 404 */}
              <div className="relative select-none pointer-events-none">
                <span className="text-[180px] lg:text-[220px] font-black leading-none tracking-tighter text-[#f0f0f0]">
                  404
                </span>
                <span className="absolute inset-0 flex items-center justify-center text-[180px] lg:text-[220px] font-black leading-none tracking-tighter text-primary/10">
                  404
                </span>
              </div>

              {/* Quick Links */}
              <div className="w-full lg:max-w-xs space-y-3">
                <p className="text-xs font-black uppercase tracking-wider text-gray-400 mb-4">Explore instead</p>
                {suggestions.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    className="flex items-center justify-between w-full bg-[#f8f8f8] hover:bg-primary/5 rounded-xl px-5 py-4 group transition-all duration-200 cursor-pointer"
                  >
                    <span className="text-sm font-bold text-black group-hover:text-primary transition-colors uppercase tracking-wider">
                      {s.label}
                    </span>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all text-base"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
